import dayjs, { Dayjs } from "dayjs";
import { osName, browserName, mobileVendor } from "react-device-detect";

import { HotJar } from "./hotjar";
import { MixPanel, specialSuperProperties } from "./mixpanel";

interface Properties {
  [key: string]: string | string[] | number | boolean | undefined;
}

interface NumberProperties {
  [key: string]: number;
}

interface Options {
  mixPanel?: boolean;
  hotJar?: boolean;
}

export const Analytics = {
  getDistinctID: (): string | undefined => MixPanel?.get_distinct_id(),
  getSessionID: (): string | undefined => MixPanel?.get_property(specialSuperProperties.sessionIdKey),

  alias: (userId: string, oldUserId?: string, options?: Partial<Options>) => {
    const { mixPanel } = getOptions(options);

    if (mixPanel) {
      MixPanel?.alias(userId, oldUserId || MixPanel?.get_distinct_id());
    }
  },
  identify: (userId: string, properties?: Properties, options?: Partial<Options>) => {
    const { mixPanel, hotJar } = getOptions(options);

    if (mixPanel) {
      MixPanel?.identify(userId);
    }

    if (hotJar) {
      HotJar?.identify(userId, properties || { name: `User#${userId}` });
    }
  },
  track: (event: string, properties?: Properties, options?: Partial<Options>) => {
    const { mixPanel } = getOptions(options);

    if (mixPanel) {
      MixPanel?.track(event, properties);
    }
  },
  sendMediaPermissionEvent,
  getSuperProperty: (name: string) => MixPanel?.get_property(name),
  setSuperProperties: (properties: Properties, options?: Partial<Options>) => {
    const { mixPanel } = getOptions(options);

    if (mixPanel) {
      MixPanel?.register(properties);
    }
  },
  setSuperPropertiesOnce: (properties: Properties, options?: Partial<Options>) => {
    const { mixPanel } = getOptions(options);

    if (mixPanel) {
      MixPanel?.register_once(properties);
    }
  },
  setUserProperties: (properties: Properties, options?: Partial<Options>) => {
    const { mixPanel } = getOptions(options);

    if (mixPanel) {
      MixPanel?.people.set(properties);
    }
  },
  setUserPropertiesOnce: (properties: Properties, options?: Partial<Options>) => {
    const { mixPanel } = getOptions(options);

    if (mixPanel) {
      MixPanel?.people.set_once(properties);
    }
  },
  incrementUserProperties: (properties: NumberProperties, options?: Partial<Options>) => {
    const { mixPanel } = getOptions(options);

    if (mixPanel) {
      MixPanel?.people.increment(properties);
    }
  },
  incrementSuperProperties: (properties: NumberProperties, options?: Partial<Options>) => {
    const { mixPanel } = getOptions(options);

    if (mixPanel) {
      const data = {} as NumberProperties;

      Object.entries(properties).forEach(([key, value]) => {
        const currentValue = MixPanel?.get_property(key);

        if (currentValue && typeof currentValue === "number") {
          data[key] = currentValue + value;
        } else {
          data[key] = value;
        }
      });

      MixPanel?.register(data);
    }
  },
};

function sendMediaPermissionEvent(status: "Accepted" | "Denied", screenName: string, options?: Partial<Options>) {
  Analytics.track(
    `Media Permissions ${status}`,
    {
      OS: osName,
      Browser: browserName,
      Device: mobileVendor,
      "Screen Name": screenName,
    },
    options
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getSuperProperties() {
  const utmProperties = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].map(
    (name) => `${name} [Last Visit]`
  );

  const superPropertiesNames = [
    ...utmProperties,
    "Total Friends",
    "Phone Number",
    "Registration Date",
    "First Room Visit Date",
    "Rooms Visited Lifetime",
    "User Profile Click",
    "Participant Profile-Block Click",
    "Participant Profile View",
    ...Object.values(specialSuperProperties),
  ];

  const properties = {} as Properties;

  superPropertiesNames.forEach((propertyName) => {
    const value = MixPanel?.get_property(propertyName);

    if (value !== undefined) {
      properties[propertyName] = value;
    }
  });

  return properties;
}

function getOptions(options?: Partial<Options>) {
  return {
    mixPanel: true,
    hotJar: true,
    ...options,
  };
}

// Room statistics data and functions

export interface RoomStatistic {
  startCall: Dayjs;
  participants: Set<number>;
  starred: Set<number>;
  blocked: Set<number>;
}

export const roomStatistic: RoomStatistic = {
  startCall: dayjs(),
  participants: new Set<number>(),
  starred: new Set<number>(),
  blocked: new Set<number>(),
};

export const getRoomStatistic = () => {
  const { startCall, participants, starred, blocked } = roomStatistic;

  return {
    "Time in Room": dayjs().diff(startCall, "minutes"),
    "Room Participants": participants.size + 1,
    "Friends in Room": starred.size,
    "Blocked in Room": blocked.size,
  };
};

export const resetRoomStatistic = () => {
  roomStatistic.startCall = dayjs();
  roomStatistic.participants = new Set<number>();
  roomStatistic.starred = new Set<number>();
  roomStatistic.blocked = new Set<number>();
};
